import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import { PropertyListItem } from "../../../models/Property/Property";

interface PropertyTableProps {
  properties: PropertyListItem[];
  maxHeight?: number;
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
}

const PropertyTable: React.FC<PropertyTableProps> = ({
  properties,
  maxHeight = window.innerHeight - 350,
  page,
  limit,
  total_count,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {properties.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    title="ID"
                    fontSize="14"
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      minWidth: 80,
                      maxWidth: 80,
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                    }}
                  />
                  <AppTableHeaderCell
                    title="Name"
                    fontSize="14"
                    minWidth="180px"
                    sx={{
                      minWidth: 180,
                      maxWidth: 280,
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                    }}
                  />
                   <AppTableHeaderCell
                    title="Image Count"
                    fontSize="14"
                    minWidth="80px"
                    sx={{
                      minWidth: 80,
                      maxWidth: 80,
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                    }}
                  />
                  <AppTableHeaderCell
                    title="Description"
                    fontSize="14"
                    minWidth="260px"
                    sx={{
                      minWidth: 260,
                      maxWidth: 400,
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                    }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {properties.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                  >
                    <AppTableRowCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 15,
                        minWidth: 80,
                        maxWidth: 80,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                        fontSize: "12px",
                        fontWeight: 600,
                        color:
                          theme.palette.customColors?.greyTextColor ||
                          theme.palette.text.secondary,
                      }}
                    >
                      <Link
                        to={`/property/edit/${row.id}`}
                        style={{
                          color: "#6455ED",
                          textDecoration: "none",
                        }}
                      >
                        {row.id}
                      </Link>
                    </AppTableRowCell>
                    <AppTableRowCell
                      sx={{
                        fontSize: "12px",
                        minWidth: 180,
                        maxWidth: 280,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.name ?? "—"}
                    </AppTableRowCell>
                     <AppTableRowCell
                      sx={{
                        fontSize: "12px",
                        minWidth: 80,
                        maxWidth: 80,
                      }}
                    >
                      {row.image_count || 0}
                    </AppTableRowCell>
                    <AppTableRowCell
                      sx={{
                        fontSize: "12px",
                        minWidth: 260,
                        maxWidth: 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.description ?? "—"}
                    </AppTableRowCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default PropertyTable;
